import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery, useMutation, useQueryClient, queryOptions } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Suspense, useMemo, useState } from "react";

import {
  getTourInventory,
  upsertSchedule,
  deleteSchedule,
  generateAvailability,
  updateAvailability,
  deleteAvailability,
  addBlockedDate,
  removeBlockedDate,
  upsertMeetingPoint,
  deleteMeetingPoint,
  upsertSeason,
  deleteSeason,
  setSeasonPricing,
} from "@/lib/admin-inventory.functions";
import { SiteHeader } from "@/components/site/SiteHeader";

const invQuery = (id: string) =>
  queryOptions({
    queryKey: ["admin", "tour-inventory", id],
    queryFn: () => getTourInventory({ data: { tourId: id } }),
  });

export const Route = createFileRoute("/_authenticated/admin/tours/$id/inventory")({
  head: () => ({
    meta: [
      { title: "Inventario del tour — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: InventoryPage,
});

function InventoryPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
        <Suspense fallback={<div className="text-muted-foreground">Cargando…</div>}>
          <InventoryBody />
        </Suspense>
      </div>
    </div>
  );
}

const DAYS = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
const TIERS = [
  { v: "adult", label: "Adulto" },
  { v: "child", label: "Menor" },
  { v: "infant", label: "Infante" },
  { v: "student", label: "Estudiante" },
  { v: "senior", label: "3ra edad" },
  { v: "private", label: "Privado" },
  { v: "group", label: "Grupal" },
] as const;

function InventoryBody() {
  const { id } = Route.useParams();
  const { data } = useSuspenseQuery(invQuery(id));
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "tour-inventory", id] });

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between gap-4">
        <div>
          <Link to="/admin/tours" className="text-sm text-muted-foreground hover:text-foreground">← Tours</Link>
          <h1 className="mt-1 font-display text-3xl font-semibold">Inventario · {data.tour.title}</h1>
          <p className="text-sm text-muted-foreground">Horarios, disponibilidad, cupos, fechas bloqueadas, puntos de encuentro y tarifas por temporada.</p>
        </div>
      </div>

      <SchedulesSection tourId={id} schedules={data.schedules} onChange={invalidate} />
      <AvailabilitySection tourId={id} availability={data.availability} onChange={invalidate} />
      <BlockedDatesSection tourId={id} blocked={data.blockedDates} onChange={invalidate} />
      <MeetingPointsSection tourId={id} points={data.meetingPoints} onChange={invalidate} />
      <SeasonsSection tourId={id} seasons={data.seasons} onChange={invalidate} />
    </div>
  );
}

function Section({ title, description, action, children }: { title: string; description?: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-border bg-card p-6">
      <header className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-xl font-semibold">{title}</h2>
          {description && <p className="text-sm text-muted-foreground">{description}</p>}
        </div>
        {action}
      </header>
      {children}
    </section>
  );
}

// ============ Schedules ===================================================
function SchedulesSection({ tourId, schedules, onChange }: { tourId: string; schedules: any[]; onChange: () => void }) {
  const save = useServerFn(upsertSchedule);
  const del = useServerFn(deleteSchedule);
  const [draft, setDraft] = useState<any | null>(null);

  const saveMut = useMutation({ mutationFn: save, onSuccess: () => { onChange(); setDraft(null); } });
  const delMut = useMutation({ mutationFn: del, onSuccess: onChange });

  return (
    <Section
      title="Horarios"
      description="Plantillas recurrentes (hora + días de la semana + cupo por defecto)."
      action={<button onClick={() => setDraft({ start_time: "09:00", days_of_week: [1,2,3,4,5,6], default_capacity: 10, active: true })} className="rounded-lg bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground">+ Horario</button>}
    >
      {schedules.length === 0 && <p className="text-sm text-muted-foreground">Aún no hay horarios.</p>}
      <ul className="divide-y divide-border">
        {schedules.map((s) => (
          <li key={s.id} className="flex items-center justify-between gap-4 py-3 text-sm">
            <div>
              <span className="font-semibold">{(s.start_time as string).slice(0,5)}</span>
              <span className="ml-2 text-muted-foreground">{(s.days_of_week as number[]).map((d) => DAYS[d]).join(" · ")}</span>
              <span className="ml-2 text-muted-foreground">· cupo {s.default_capacity}</span>
              {!s.active && <span className="ml-2 rounded bg-muted px-2 py-0.5 text-xs">inactivo</span>}
              {s.label && <span className="ml-2 text-muted-foreground">· {s.label}</span>}
            </div>
            <div className="flex gap-3">
              <button onClick={() => setDraft({ ...s, start_time: (s.start_time as string).slice(0,5) })} className="text-primary hover:underline">Editar</button>
              <button onClick={() => { if (confirm("¿Eliminar horario?")) delMut.mutate({ data: { id: s.id } }); }} className="text-destructive hover:underline">Eliminar</button>
            </div>
          </li>
        ))}
      </ul>

      {draft && (
        <form
          onSubmit={(e) => { e.preventDefault(); saveMut.mutate({ data: { ...draft, tour_id: tourId } }); }}
          className="mt-4 grid gap-3 rounded-xl border border-border p-4 sm:grid-cols-2"
        >
          <label className="block text-sm">Hora
            <input type="time" required value={draft.start_time} onChange={(e) => setDraft({ ...draft, start_time: e.target.value })} className="mt-1 h-10 w-full rounded-lg border border-border bg-background px-2" />
          </label>
          <label className="block text-sm">Cupo por defecto
            <input type="number" min={0} required value={draft.default_capacity} onChange={(e) => setDraft({ ...draft, default_capacity: Number(e.target.value) })} className="mt-1 h-10 w-full rounded-lg border border-border bg-background px-2" />
          </label>
          <fieldset className="sm:col-span-2">
            <legend className="text-sm">Días de la semana</legend>
            <div className="mt-1 flex flex-wrap gap-2">
              {DAYS.map((d, i) => {
                const on = draft.days_of_week.includes(i);
                return (
                  <button type="button" key={i} onClick={() => setDraft({ ...draft, days_of_week: on ? draft.days_of_week.filter((x: number) => x !== i) : [...draft.days_of_week, i].sort() })}
                    className={`rounded-full px-3 py-1 text-xs ${on ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"}`}>{d}</button>
                );
              })}
            </div>
          </fieldset>
          <label className="block text-sm sm:col-span-2">Etiqueta (opcional)
            <input value={draft.label ?? ""} onChange={(e) => setDraft({ ...draft, label: e.target.value })} className="mt-1 h-10 w-full rounded-lg border border-border bg-background px-2" placeholder="p.ej. matutino" />
          </label>
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input type="checkbox" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} />
            Activo
          </label>
          <div className="flex gap-2 sm:col-span-2">
            <button type="submit" disabled={saveMut.isPending} className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60">Guardar</button>
            <button type="button" onClick={() => setDraft(null)} className="rounded-lg border border-border px-4 py-2 text-sm">Cancelar</button>
          </div>
        </form>
      )}
    </Section>
  );
}

// ============ Availability ================================================
function AvailabilitySection({ tourId, availability, onChange }: { tourId: string; availability: any[]; onChange: () => void }) {
  const gen = useServerFn(generateAvailability);
  const upd = useServerFn(updateAvailability);
  const del = useServerFn(deleteAvailability);
  const today = new Date().toISOString().slice(0, 10);
  const in30 = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
  const [range, setRange] = useState({ start: today, end: in30, overwrite: false });
  const genMut = useMutation({ mutationFn: gen, onSuccess: onChange });
  const updMut = useMutation({ mutationFn: upd, onSuccess: onChange });
  const delMut = useMutation({ mutationFn: del, onSuccess: onChange });

  const grouped = useMemo(() => {
    const map = new Map<string, any[]>();
    for (const s of availability) {
      const k = s.date;
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(s);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [availability]);

  return (
    <Section title="Disponibilidad" description="Genera lugares por fecha a partir de los horarios activos. Edita el cupo, ciérralos o elimínalos individualmente.">
      <form onSubmit={(e) => { e.preventDefault(); genMut.mutate({ data: { tourId, startDate: range.start, endDate: range.end, overwrite: range.overwrite } }); }} className="flex flex-wrap items-end gap-3 rounded-xl border border-border p-4">
        <label className="text-sm">Desde<input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} className="ml-2 h-9 rounded-lg border border-border bg-background px-2" /></label>
        <label className="text-sm">Hasta<input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} className="ml-2 h-9 rounded-lg border border-border bg-background px-2" /></label>
        <label className="text-sm inline-flex items-center gap-2"><input type="checkbox" checked={range.overwrite} onChange={(e) => setRange({ ...range, overwrite: e.target.checked })} />Re-generar (borra lugares sin venta)</label>
        <button type="submit" disabled={genMut.isPending} className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60">Generar</button>
        {genMut.isSuccess && <span className="text-xs text-muted-foreground">+{(genMut.data as any)?.created ?? 0} lugares</span>}
      </form>

      <div className="mt-4 max-h-[440px] overflow-y-auto rounded-xl border border-border">
        {grouped.length === 0 && <p className="p-4 text-sm text-muted-foreground">Sin disponibilidad cargada. Usa "Generar" arriba.</p>}
        {grouped.map(([date, slots]) => (
          <div key={date} className="border-b border-border last:border-0">
            <div className="bg-muted/50 px-4 py-2 text-sm font-semibold">{date}</div>
            <ul className="divide-y divide-border">
              {slots.map((s) => (
                <li key={s.id} className="flex flex-wrap items-center gap-3 px-4 py-2 text-sm">
                  <span className="font-mono">{(s.start_time as string).slice(0,5)}</span>
                  <label className="text-xs text-muted-foreground">cupo<input type="number" min={s.sold} defaultValue={s.capacity}
                    onBlur={(e) => { const v = Number(e.target.value); if (v !== s.capacity) updMut.mutate({ data: { id: s.id, capacity: v } }); }}
                    className="ml-1 h-8 w-20 rounded border border-border bg-background px-2" /></label>
                  <span className="text-xs text-muted-foreground">vendidos: {s.sold}</span>
                  <select value={s.status} onChange={(e) => updMut.mutate({ data: { id: s.id, status: e.target.value as any } })} className="h-8 rounded border border-border bg-background px-2 text-xs">
                    <option value="open">abierto</option>
                    <option value="closed">cerrado</option>
                    <option value="sold_out">agotado</option>
                  </select>
                  <button onClick={() => { if (confirm("¿Eliminar este lugar?")) delMut.mutate({ data: { id: s.id } }); }} className="ml-auto text-xs text-destructive hover:underline">Eliminar</button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </Section>
  );
}

// ============ Blocked dates ===============================================
function BlockedDatesSection({ tourId, blocked, onChange }: { tourId: string; blocked: any[]; onChange: () => void }) {
  const add = useServerFn(addBlockedDate);
  const rem = useServerFn(removeBlockedDate);
  const [date, setDate] = useState("");
  const [reason, setReason] = useState("");
  const addMut = useMutation({ mutationFn: add, onSuccess: () => { onChange(); setDate(""); setReason(""); } });
  const remMut = useMutation({ mutationFn: rem, onSuccess: onChange });

  return (
    <Section title="Fechas bloqueadas" description="Bloquea un día entero (clima, mantenimiento, festivos).">
      <form onSubmit={(e) => { e.preventDefault(); if (date) addMut.mutate({ data: { tourId, date, reason: reason || undefined } }); }} className="flex flex-wrap items-end gap-3 rounded-xl border border-border p-4">
        <input type="date" required value={date} onChange={(e) => setDate(e.target.value)} className="h-9 rounded-lg border border-border bg-background px-2 text-sm" />
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivo (opcional)" className="h-9 flex-1 rounded-lg border border-border bg-background px-2 text-sm" />
        <button type="submit" disabled={addMut.isPending} className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60">Bloquear</button>
      </form>
      {blocked.length > 0 && (
        <ul className="mt-3 divide-y divide-border rounded-xl border border-border">
          {blocked.map((b) => (
            <li key={b.id} className="flex items-center justify-between gap-3 px-4 py-2 text-sm">
              <span><span className="font-mono">{b.date}</span>{b.reason && <span className="ml-2 text-muted-foreground">— {b.reason}</span>}</span>
              <button onClick={() => remMut.mutate({ data: { id: b.id } })} className="text-xs text-destructive hover:underline">Desbloquear</button>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

// ============ Meeting Points ==============================================
function MeetingPointsSection({ tourId, points, onChange }: { tourId: string; points: any[]; onChange: () => void }) {
  const save = useServerFn(upsertMeetingPoint);
  const del = useServerFn(deleteMeetingPoint);
  const [draft, setDraft] = useState<any | null>(null);
  const saveMut = useMutation({ mutationFn: save, onSuccess: () => { onChange(); setDraft(null); } });
  const delMut = useMutation({ mutationFn: del, onSuccess: onChange });

  return (
    <Section
      title="Puntos de encuentro y recogidas"
      action={<button onClick={() => setDraft({ kind: "meeting", name: "", extra_price: 0, position: points.length, active: true })} className="rounded-lg bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground">+ Punto</button>}
    >
      {points.length === 0 && <p className="text-sm text-muted-foreground">Aún no hay puntos.</p>}
      <ul className="divide-y divide-border">
        {points.map((p) => (
          <li key={p.id} className="flex items-start justify-between gap-3 py-3 text-sm">
            <div>
              <div className="font-semibold">{p.name} <span className="ml-2 text-xs text-muted-foreground">({p.kind === "pickup" ? "Recogida" : "Encuentro"})</span></div>
              {p.address && <div className="text-muted-foreground">{p.address}</div>}
              {Number(p.extra_price) > 0 && <div className="text-xs text-muted-foreground">+ ${p.extra_price} MXN</div>}
            </div>
            <div className="flex gap-3">
              <button onClick={() => setDraft(p)} className="text-primary hover:underline">Editar</button>
              <button onClick={() => { if (confirm("¿Eliminar?")) delMut.mutate({ data: { id: p.id } }); }} className="text-destructive hover:underline">Eliminar</button>
            </div>
          </li>
        ))}
      </ul>
      {draft && (
        <form onSubmit={(e) => { e.preventDefault(); saveMut.mutate({ data: { ...draft, tour_id: tourId, extra_price: Number(draft.extra_price) || 0, position: Number(draft.position) || 0 } }); }} className="mt-4 grid gap-3 rounded-xl border border-border p-4 sm:grid-cols-2">
          <label className="text-sm">Tipo
            <select value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value })} className="mt-1 h-10 w-full rounded-lg border border-border bg-background px-2">
              <option value="meeting">Punto de encuentro</option>
              <option value="pickup">Recogida en hotel</option>
            </select>
          </label>
          <label className="text-sm">Costo adicional (MXN)
            <input type="number" min={0} step="0.01" value={draft.extra_price ?? 0} onChange={(e) => setDraft({ ...draft, extra_price: e.target.value })} className="mt-1 h-10 w-full rounded-lg border border-border bg-background px-2" />
          </label>
          <label className="text-sm sm:col-span-2">Nombre
            <input required value={draft.name ?? ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="mt-1 h-10 w-full rounded-lg border border-border bg-background px-2" />
          </label>
          <label className="text-sm sm:col-span-2">Dirección
            <input value={draft.address ?? ""} onChange={(e) => setDraft({ ...draft, address: e.target.value })} className="mt-1 h-10 w-full rounded-lg border border-border bg-background px-2" />
          </label>
          <div className="flex gap-2 sm:col-span-2">
            <button type="submit" disabled={saveMut.isPending} className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60">Guardar</button>
            <button type="button" onClick={() => setDraft(null)} className="rounded-lg border border-border px-4 py-2 text-sm">Cancelar</button>
          </div>
        </form>
      )}
    </Section>
  );
}

// ============ Seasons & Pricing ===========================================
function SeasonsSection({ tourId, seasons, onChange }: { tourId: string; seasons: any[]; onChange: () => void }) {
  const save = useServerFn(upsertSeason);
  const del = useServerFn(deleteSeason);
  const setPricing = useServerFn(setSeasonPricing);
  const [draft, setDraft] = useState<any | null>(null);
  const saveMut = useMutation({ mutationFn: save, onSuccess: () => { onChange(); setDraft(null); } });
  const delMut = useMutation({ mutationFn: del, onSuccess: onChange });
  const priceMut = useMutation({ mutationFn: setPricing, onSuccess: onChange });

  return (
    <Section
      title="Temporadas y tarifas"
      description="Define temporadas (alta/baja/festivos) y precios por categoría. La de mayor prioridad cubriendo la fecha es la que aplica."
      action={<button onClick={() => setDraft({ name: "", start_date: "", end_date: "", priority: 0, active: true })} className="rounded-lg bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground">+ Temporada</button>}
    >
      {seasons.length === 0 && <p className="text-sm text-muted-foreground">Aún no hay temporadas. Crea al menos una "Estándar" cubriendo todo el año.</p>}
      <div className="space-y-4">
        {seasons.map((s) => (
          <div key={s.id} className="rounded-xl border border-border p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-semibold">{s.name} <span className="ml-2 text-xs text-muted-foreground">prioridad {s.priority}{!s.active && " · inactiva"}</span></div>
                <div className="text-sm text-muted-foreground">{s.start_date} → {s.end_date}</div>
              </div>
              <div className="flex gap-3 text-sm">
                <button onClick={() => setDraft(s)} className="text-primary hover:underline">Editar</button>
                <button onClick={() => { if (confirm("¿Eliminar temporada?")) delMut.mutate({ data: { id: s.id } }); }} className="text-destructive hover:underline">Eliminar</button>
              </div>
            </div>
            <SeasonPricingEditor season={s} onSave={(pricing) => priceMut.mutate({ data: { season_id: s.id, pricing } })} pending={priceMut.isPending} />
          </div>
        ))}
      </div>

      {draft && (
        <form onSubmit={(e) => { e.preventDefault(); saveMut.mutate({ data: { ...draft, tour_id: tourId, priority: Number(draft.priority) || 0 } }); }} className="mt-4 grid gap-3 rounded-xl border border-border p-4 sm:grid-cols-2">
          <label className="text-sm sm:col-span-2">Nombre<input required value={draft.name ?? ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="mt-1 h-10 w-full rounded-lg border border-border bg-background px-2" /></label>
          <label className="text-sm">Inicio<input required type="date" value={draft.start_date ?? ""} onChange={(e) => setDraft({ ...draft, start_date: e.target.value })} className="mt-1 h-10 w-full rounded-lg border border-border bg-background px-2" /></label>
          <label className="text-sm">Fin<input required type="date" value={draft.end_date ?? ""} onChange={(e) => setDraft({ ...draft, end_date: e.target.value })} className="mt-1 h-10 w-full rounded-lg border border-border bg-background px-2" /></label>
          <label className="text-sm">Prioridad<input type="number" value={draft.priority ?? 0} onChange={(e) => setDraft({ ...draft, priority: e.target.value })} className="mt-1 h-10 w-full rounded-lg border border-border bg-background px-2" /></label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} />Activa</label>
          <div className="flex gap-2 sm:col-span-2">
            <button type="submit" disabled={saveMut.isPending} className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60">Guardar</button>
            <button type="button" onClick={() => setDraft(null)} className="rounded-lg border border-border px-4 py-2 text-sm">Cancelar</button>
          </div>
        </form>
      )}
    </Section>
  );
}

function SeasonPricingEditor({ season, onSave, pending }: { season: any; onSave: (p: any[]) => void; pending: boolean }) {
  const initial = useMemo(() => {
    const map: Record<string, number> = {};
    for (const p of (season.custom_tour_season_pricing ?? [])) map[p.tier] = Number(p.base_price);
    return map;
  }, [season]);
  const [prices, setPrices] = useState<Record<string, string>>(() => Object.fromEntries(TIERS.map((t) => [t.v, initial[t.v] ? String(initial[t.v]) : ""])));

  return (
    <div className="mt-3 border-t border-border pt-3">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Tarifas (MXN)</div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {TIERS.map((t) => (
          <label key={t.v} className="text-xs">
            <span className="text-muted-foreground">{t.label}</span>
            <input type="number" min={0} step="0.01" value={prices[t.v]} onChange={(e) => setPrices({ ...prices, [t.v]: e.target.value })} className="mt-0.5 h-9 w-full rounded border border-border bg-background px-2 text-sm" placeholder="—" />
          </label>
        ))}
      </div>
      <button
        type="button"
        disabled={pending}
        onClick={() => {
          const out = TIERS
            .filter((t) => prices[t.v] !== "" && !Number.isNaN(Number(prices[t.v])))
            .map((t) => ({ tier: t.v, base_price: Number(prices[t.v]), currency: "MXN" }));
          onSave(out);
        }}
        className="mt-3 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground disabled:opacity-60"
      >
        Guardar tarifas
      </button>
    </div>
  );
}
